import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Details,
  SubHeading,
} from '~/components/compositional/DetailViewStyles'
import Text, { FontStyle } from '~/components/core/Text'
import { AudioPlayer } from '~/components/shared/AudioPlayer'
import { AudioPlayerVariant } from '~/components/shared/componentStyles/AudioPlayer.style'
import { formatRelativeDay, phoneFormatter } from '~/utils/formatter'

import { formatTimeString } from '@mangovoice/core'
import { VoicemailInstance } from '@mangovoice/types'

import { DateWrapper, RootWrapper, Wrapper } from './Voicemail.styles'

interface VoicemailDetailHeaderProps {
  caller_id_name: string
  caller_id_number: string
  selectedVoicemailBoxName?: string
}
export const VoicemailDetailHeader = ({
  caller_id_name,
  caller_id_number,
  selectedVoicemailBoxName,
}: VoicemailDetailHeaderProps) => {
  const { t } = useTranslation()

  return (
    <Details>
      <Text
        fontStyle={FontStyle.MediumBold}
        text={caller_id_name}
        color="var(--chakra-colors-fg-default)"
      />
      <SubHeading>
        <Text
          fontStyle={FontStyle.Small}
          text={t('voicemail.detailView.headerSubheadingText')}
          color="var(--chakra-colors-fg-subtle)"
        />
        <Text
          style={{ marginLeft: '4px' }}
          fontStyle={FontStyle.bSmall}
          text={phoneFormatter(caller_id_number)}
          color="var(--chakra-colors-fg-muted)"
        />
        <Text
          style={{ marginLeft: '4px' }}
          fontStyle={FontStyle.Small}
          text={t('voicemail.detailView.headerSubheadingToText')}
          color="var(--chakra-colors-fg-subtle)"
        />
        <Text
          style={{ marginLeft: '4px' }}
          fontStyle={FontStyle.bSmall}
          text={selectedVoicemailBoxName ?? ''}
          color="var(--chakra-colors-fg-muted)"
        />
      </SubHeading>
    </Details>
  )
}

interface VoicemailDetailViewProps {
  selectedVoicemail: VoicemailInstance
  audioTimestamp?: number
  forceDisableAudioPlayerKeyboardInput?: boolean
  isAnyModalOpen?: boolean
  onAudioPlayerPositionChange: (position: number) => void
  onPlaybackStarted?: () => void
  onPlaybackLoadError?: () => void
}
export const VoicemailDetailView = ({
  selectedVoicemail,
  audioTimestamp,
  forceDisableAudioPlayerKeyboardInput,
  isAnyModalOpen,
  onAudioPlayerPositionChange: handleAudioPlayerPositionChange,
  onPlaybackStarted,
  onPlaybackLoadError,
}: VoicemailDetailViewProps) => {
  const { t } = useTranslation()
  // handles disabling keyboard input for audio player when any modal is open or when forceDisableAudioPlayerKeyboardInput is true
  const isPlayerDisabled =
    isAnyModalOpen || forceDisableAudioPlayerKeyboardInput

  const { created_at, transcription, file_url } = selectedVoicemail ?? {}
  const date = formatRelativeDay(created_at)
  const time = formatTimeString(created_at)
  return (
    <RootWrapper>
      <Wrapper>
        <DateWrapper>
          <Text
            fontStyle={FontStyle.Tiny}
            text={date}
            color="var(--chakra-colors-fg-muted)"
          />
          <Text
            fontStyle={FontStyle.Tiny}
            text={time}
            color="var(--chakra-colors-fg-muted)"
          />
        </DateWrapper>
        <AudioPlayer
          disabled={isPlayerDisabled}
          audioUri={file_url ?? ''}
          playerTitle={t('voicemail.detailView.audioPlayerTitle')}
          variant={AudioPlayerVariant.Stacked}
          transcriptionText={transcription}
          requestedTimestamp={audioTimestamp}
          onValueChanged={handleAudioPlayerPositionChange}
          onPlayStateChange={(isPlaying) => {
            if (isPlaying) {
              onPlaybackStarted?.()
            }
          }}
          onLoadError={onPlaybackLoadError}
        />
      </Wrapper>
    </RootWrapper>
  )
}
